import React, { memo, useMemo, useState, useEffect } from 'react';
import { Stack, Typography, Icon, Button, Tooltip } from 'investira.react.components';
import { validators, formats } from 'investira.sdk';
import DataTable from '../DataTable';
import withMessage from '../../../hoc/withMessage';

const CorrelacaoMatriz = memo(props => {
  const { matriz, itens } = props;

  const [columnVisibility, setColumnVisibility] = useState({});

  // Inicializa columnVisibility quando xItens mudar
  useEffect(() => {
    if (itens && (Array.isArray(itens) || validators.isObject(itens))) {
      const xVisibility = {};
      const xItemsArray = Array.isArray(itens) ? itens : Object.values(itens);

      xItemsArray.forEach(xAtivo => {
        console.log(xAtivo);
        const key = xAtivo?.id || xAtivo?.ticker || xAtivo?.codigo || xAtivo?.ativo_seuid;
        xVisibility[`${key}_variacao_acumulada`] = false;
        xVisibility[`${key}_taxa`] = ['J', 'C'].includes(xAtivo.indicador_tipo_id);
        xVisibility[`${key}_cotacao`] = !['J', 'C'].includes(xAtivo.indicador_tipo_id);
      });

      setColumnVisibility(xVisibility);
    }
  }, [itens]);

  // Esta função renderiza o tooltip para uma célula da matriz.
  // Recebe a linha (pRow) e o ativo (pAtivo). Retorna uma string formatada.
  function renderCellTooltip(pRow, pAtivo) {
    // Consulta formatNumber de investira.sdk.formats para formatação

    // Padrão de keys predefinidos pelo formato da matriz
    const xCotacao = pRow?.[`${pAtivo.key}_cotacao`];
    const xVariacao = pRow?.[`${pAtivo.key}_variacao`];

    // Se não tem nenhum dado, retorna string vazia
    if (validators.isNull(xCotacao) && validators.isNull(xVariacao)) {
      return '';
    }

    let xTooltip = '';

    // Mostra Cotação se disponível
    if (!validators.isNull(xCotacao)) {
      xTooltip += `Cotação: ${formats.formatNumber(xCotacao, 6, true)}`;
    }

    // Mostra Variação se disponível
    if (!validators.isNull(xVariacao)) {
      if (xTooltip) xTooltip += ' ';
      xTooltip += `Variação: ${formats.formatNumber(xVariacao, 6, true)}`;
    }

    return xTooltip;
  }

  // Normaliza os nomes dos ativos
  const xItens = useMemo(() => {
    if (validators.isObject(itens)) {
      return Object.values(itens).map(xAtivo => {
        return {
          ...xAtivo,
          key: xAtivo?.id || xAtivo?.ticker || xAtivo?.codigo || xAtivo?.ativo_seuid
        };
      });
    }
    return [];
  }, [itens]);

  // Formata dados vindos do matriz_mock.js para array de linhas para DataTable
  const xData = useMemo(() => {
    if (!matriz || typeof matriz !== 'object') return [];

    return Object.entries(matriz)
      .map(([xDataStr, xObj]) => {
        let xLinha = { data: xDataStr };

        xItens.forEach(xAtivo => {
          const xKey = xAtivo.key;
          if (xObj && xObj[xKey]) {
            xLinha[`${xKey}_cotacao`] = xObj[xKey].cotacao;
            xLinha[`${xKey}_taxa`] = xObj[xKey].taxa;
            xLinha[`${xKey}_variacao`] = xObj[xKey].variacao;
            xLinha[`${xKey}_variacao_acumulada`] = xObj[xKey].variacao_acumulada;
          } else {
            xLinha[`${xKey}_cotacao`] = null;
            xLinha[`${xKey}_taxa`] = null;
            xLinha[`${xKey}_variacao`] = null;
            xLinha[`${xKey}_variacao_acumulada`] = null;
          }
        });
        return xLinha;
      })
      .reverse();
  }, [matriz, xItens]);

  const getCellHeader = pStr => {
    const LABEL_SIZE = 14;
    let xLabel = pStr;
    let xShortLabel = pStr;

    if (pStr.length > LABEL_SIZE) {
      xShortLabel = xLabel.slice(0, LABEL_SIZE) + '...';
    }

    return { label: xLabel, 'short-label': xShortLabel };
  };

  // Colunas iniciais (Data)
  const xColumns = useMemo(() => {
    const xCols = [
      {
        accessorKey: 'data',
        header: 'Data',
        label: 'Data',
        maxSize: 80,
        enableColumnActions: false,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue() ? formats.formatDate(cell.getValue(), 'DD/MM/YYYY') : cell.getValue()
      }
    ];

    // Para cada Ativo, adicionar colunas para cotação, variação e variação acumulada
    xItens.forEach(xAtivo => {
      // Cotação
      xCols.push({
        accessorKey: `${xAtivo.key}_cotacao`,
        id: `${xAtivo.key}_cotacao`,
        label: `${
          getCellHeader(xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id)?.[
            'short-label'
          ]
        } Cotação`,
        header: (
          <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight="bold">
              {
                getCellHeader(
                  xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id
                )?.['short-label']
              }
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Cotação (R$)
            </Typography>
          </Stack>
        ),
        align: 'right',
        maxSize: 80,
        Cell: ({ cell }) =>
          validators.isNull(cell.getValue())
            ? ''
            : cell.getValue()
            ? formats.formatNumber(cell.getValue(), 2, true)
            : cell.getValue(),
        enableColumnActions: false,
        enableHiding: true,
        visibleInShowHideMenu: true
      });
      // Taxa
      xCols.push({
        accessorKey: `${xAtivo.key}_taxa`,
        id: `${xAtivo.key}_taxa`,
        label: `${
          getCellHeader(xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id)?.[
            'short-label'
          ]
        } Taxa`,
        header: (
          <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight="bold">
              {
                getCellHeader(
                  xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id
                )?.['short-label']
              }
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Taxa (%)
            </Typography>
          </Stack>
        ),
        align: 'right',
        maxSize: 80,
        Cell: ({ cell }) =>
          validators.isNull(cell.getValue())
            ? ''
            : cell.getValue()
            ? formats.formatNumber(cell.getValue(), 5, true)
            : cell.getValue(),
        enableColumnActions: false,
        enableHiding: true,
        visibleInShowHideMenu: true
      });
      // Variação
      xCols.push({
        accessorKey: `${xAtivo.key}_variacao`,
        id: `${xAtivo.key}_variacao`,
        label: `${
          getCellHeader(xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id)?.[
            'short-label'
          ]
        } Variação`,
        header: (
          <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight="bold">
              {
                getCellHeader(
                  xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id
                )?.['short-label']
              }
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Variação (%)
            </Typography>
          </Stack>
        ),
        align: 'right',
        maxSize: 90,
        Cell: ({ cell }) =>
          validators.isNull(cell.getValue()) ? (
            ''
          ) : (
            <Typography
              variant="body2"
              color={
                cell.getValue() > 0
                  ? 'success.main'
                  : cell.getValue() < 0
                  ? 'error.main'
                  : undefined
              }>
              {cell.getValue() ? formats.formatNumber(cell.getValue(), 5, true) : cell.getValue()}
            </Typography>
          ),
        enableColumnActions: false,
        enableSorting: false,
        enableHiding: true,
        visibleInShowHideMenu: true
      });
      // Variação Acumulada
      xCols.push({
        accessorKey: `${xAtivo.key}_variacao_acumulada`,
        id: `${xAtivo.key}_variacao_acumulada`,
        label: `${
          getCellHeader(xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id)?.[
            'short-label'
          ]
        } Acumulado`,
        header: (
          <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight="bold">
              {
                getCellHeader(
                  xAtivo?.ativo_seuid || xAtivo?.indicador_seuid || xAtivo?.indicador_id
                )?.['short-label']
              }
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Acumulado (%)
            </Typography>
          </Stack>
        ),
        align: 'right',
        maxSize: 90,
        Cell: ({ cell, row }) => {
          return validators.isNull(cell.getValue()) ? (
            ''
          ) : (
            <Tooltip title={renderCellTooltip(row.original, xAtivo)}>
              <span>
                {cell.getValue() ? formats.formatNumber(cell.getValue(), 6, true) : cell.getValue()}
              </span>
            </Tooltip>
          );
        },
        enableColumnActions: true,
        enableSorting: false,
        enableHiding: true,
        visibleInShowHideMenu: true
      });
    });

    return xCols;
  }, [xItens]);

  const xProps = useMemo(() => {
    return {
      columns: xColumns,
      enableFacetedValues: false,
      enableRowSelection: false,
      enableGlobalFilter: false,
      enableExpanding: false,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableBottomToolbar: true,
      enableHiding: true,
      muiTopToolbarProps: {
        sx: {
          backgroundColor: 'none',
          alignItems: 'center'
        }
      },
      initialState: {
        ...props.initialState,
        pagination: {
          pageSize: 10
        },
        columnVisibility: columnVisibility
      },
      state: {
        columnVisibility: columnVisibility
      },
      onColumnVisibilityChange: setColumnVisibility,
      displayColumnDefOptions: {
        'mrt-row-actions': {
          size: 44,
          muiTableHeadCellProps: {
            align: 'center'
          },
          muiTableBodyCellProps: {
            align: 'center'
          }
        }
      },
      renderTopToolbarCustomActions: ({ table }) => {
        const xRows = table.getRowModel().rows;

        if (!xRows || xRows.length === 0) {
          return;
        }

        return (
          <Button
            startIcon={<Icon iconName="papers" size={16} />}
            variant="contained"
            color="primary"
            onClick={() => {
              const allRows = table.getPrePaginationRowModel().rows;

              // Extrai os cabeçalhos das colunas
              // const visibleColumns = table.getVisibleLeafColumns();
              const visibleColumns = table.getAllLeafColumns();

              // console.log("allRows", allRows);
              const headers = visibleColumns
                .filter(col => col.id !== 'mrt-row-actions') // Remove coluna de ações
                .map(col => {
                  return col.columnDef.label;
                })
                .join('\t');

              const tsvData = allRows
                .map(row => {
                  return visibleColumns
                    .filter(col => col.id !== 'mrt-row-actions') // Remove coluna de ações
                    .map(col => {
                      const cell = row.getAllCells().find(c => c.column.id === col.id);

                      // Se existe uma função Cell customizada, tenta extrair o valor formatado
                      if (col.columnDef.Cell && typeof col.columnDef.Cell === 'function') {
                        try {
                          // Renderiza a célula e extrai o texto
                          const rendered = col.columnDef.Cell({ cell, row });

                          // Se for uma string simples, retorna
                          if (typeof rendered === 'string' || typeof rendered === 'number') {
                            return rendered;
                          }

                          // Se for um componente React, tenta extrair o texto
                          if (rendered && typeof rendered === 'object') {
                            // Tenta acessar props.children (comum em Typography, etc)

                            if (rendered.props?.children !== undefined) {
                              const children = rendered.props.children;

                              // Se children é object
                              if (typeof children === 'object') {
                                return children.props.children;
                              }
                              // Se children é string ou número
                              if (typeof children === 'string' || typeof children === 'number') {
                                return children;
                              }
                              // Se children é array, junta tudo
                              if (Array.isArray(children)) {
                                return children.join('');
                              }
                            }
                          }
                          // Caso contrário, usa o valor bruto
                          return cell.getValue() ?? '';
                        } catch {
                          return cell.getValue() ?? '';
                        }
                      }

                      // Retorna o valor bruto da célula
                      return cell.getValue() ?? '';
                    })
                    .join('\t');
                })
                .join('\n');

              const fullTSV = headers + '\n' + tsvData;

              navigator.clipboard.writeText(fullTSV).then(() => {
                props.onMessageSuccess(
                  `✓ ${allRows.length} linhas copiadas! Cole no Excel com Ctrl+V`
                );
              });
            }}>
            Copiar Tabela
          </Button>
        );
      }
    };
  }, [xColumns, columnVisibility, props.initialState]);

  return <DataTable data={xData} {...xProps} />;
});

export default withMessage(CorrelacaoMatriz);
